// Prompt Z (2026-09-18): high-contrast invoice detail screen, per the
// designer mockup Alian approved. VIEW mode of the invoice dialog only —
// edit mode keeps the existing form in InvoicesPage.jsx. No logic changes:
// totals, payments, payment requests and receipt sending are the same
// endpoints/handlers as before, only the presentation is new.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusStepper } from '@/components/shared/StatusStepper';
import { paymentRoundQualifier } from '@/components/common/InvoiceStatusBadge';
import DocumentsSection from '@/components/common/DocumentsSection';
import QuickLinksBar from '@/components/common/QuickLinksBar';
import { ActivityLog } from '@/components/ActivityLog';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import { fmtMoney, PAYMENT_METHOD_COLORS, PAYMENT_METHOD_LABELS } from './paymentMeta';
import {
  Receipt, Send, DollarSign, Pencil, XCircle, AlertTriangle, CreditCard,
  Plus, Printer, Check, Trash2, ArrowLeft, FileText, Files,
} from 'lucide-react';

const STATUS_PILL = {
  draft: 'bg-slate-100 text-slate-800 border-slate-300',
  sent: 'bg-sky-100 text-sky-900 border-sky-300',
  partial: 'bg-amber-100 text-amber-900 border-amber-300',
  paid: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  overdue: 'bg-rose-100 text-rose-900 border-rose-300',
  in_dispute: 'bg-rose-100 text-rose-900 border-rose-300',
  cancelled: 'bg-rose-100 text-rose-900 border-rose-300',
};
const STATUS_LABEL = {
  draft: 'Draft', sent: 'Sent', partial: 'Partially Paid', paid: 'Paid',
  overdue: 'Overdue', in_dispute: 'In Dispute', cancelled: 'Cancelled',
};

// Real invoice statuses mapped to the workflow steps — nothing invented.
// draft→Created, sent→Sent, partial→Partial, paid→Paid; cancelled is an
// EXIT from the flow (side pill, red), same pattern as the estimate stepper.
const WORKFLOW_STEPS = [
  { key: 'draft', label: 'Created' },
  { key: 'sent', label: 'Sent' },
  { key: 'partial', label: 'Partial' },
  { key: 'paid', label: 'Paid' },
  { key: 'cancelled', label: 'Cancelled', color: 'red' },
];

const stepperKey = (status) => (['overdue', 'in_dispute'].includes(status) ? 'sent' : status);

const shortDate = (iso) => {
  const raw = (iso || '').slice(0, 10);
  if (!raw) return '—';
  const d = new Date(`${raw}T12:00:00`);
  if (isNaN(d)) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Label = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{children}</p>
);

// Prompt AC CAMBIO 4 (2026-09-21): "arriba queda solo la fila de pestañas" —
// Estimate/Invoice/Files unicamente. Job/Notes/History/Permit se sacaron de
// aca (Jobs ya vive en la QuickLinksBar; Notes/History se ven scrolleando,
// no se borro ningun contenido, solo el acceso rapido duplicado).
function DocTabs({ invoice, relations, onNavigate }) {
  const estimateId = invoice.source_estimate_id || relations?.estimates?.[0]?.id;
  const tabs = [
    { key: 'estimate', label: 'Estimate', icon: FileText, enabled: !!estimateId, onClick: () => onNavigate('estimate', estimateId), testid: 'invoice-tab-estimate' },
    { key: 'invoice', label: 'Invoice', icon: Receipt, active: true, testid: 'invoice-tab-invoice' },
    { key: 'files', label: 'Files', icon: Files, enabled: true, onClick: () => onNavigate('scroll', 'invoice-section-files'), testid: 'invoice-tab-files' },
  ];
  return (
    <div data-testid="invoice-doc-tabs" className="flex items-center gap-1 flex-wrap border-b-2 border-slate-200 pb-0">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          data-testid={tab.testid}
          disabled={!tab.active && !tab.enabled}
          onClick={tab.active ? undefined : tab.onClick}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-[2px] transition-colors ${
            tab.active
              ? 'border-[#003366] text-[#003366]'
              : tab.enabled
                ? 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
                : 'border-transparent text-slate-300 cursor-not-allowed'
          }`}
        >
          <tab.icon className="w-3.5 h-3.5" /> {tab.label}
        </button>
      ))}
    </div>
  );
}

function WorkflowBlock({ invoice, canEditStatus, onStatusChange }) {
  const status = invoice.status || 'draft';
  return (
    <div data-testid="invoice-workflow-block" className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <Label>Invoice Workflow</Label>
          <div className="mt-2">
            <StatusStepper
              statuses={WORKFLOW_STEPS}
              currentKey={stepperKey(status)}
              sideKeys={['cancelled']}
              canEdit={false}
              testidPrefix="inv-step"
              listTestId="invoice-workflow-stepper"
            />
          </div>
        </div>
        <div className="flex-shrink-0 sm:w-48">
          <Label>Current Status</Label>
          <Select value={status} onValueChange={onStatusChange} disabled={!canEditStatus || ['paid', 'cancelled'].includes(status)}>
            <SelectTrigger data-testid="current-status-select" className="mt-2 h-9 bg-white font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft" data-testid="status-option-draft">Draft</SelectItem>
              <SelectItem value="sent" data-testid="status-option-sent">Sent</SelectItem>
              <SelectItem value="partial" disabled data-testid="status-option-partial">Partial (auto — payments)</SelectItem>
              <SelectItem value="paid" disabled data-testid="status-option-paid">Paid (auto — payments)</SelectItem>
              {status === 'overdue' && <SelectItem value="overdue" disabled>Overdue</SelectItem>}
              <SelectItem value="in_dispute" data-testid="status-option-in-dispute">In Dispute…</SelectItem>
              <SelectItem value="cancelled" data-testid="status-option-cancelled">Cancelled…</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Reserved slot: las "notificaciones del workflow" del mockup actualizado
          de Alian se renderizan acá. Vacío a propósito por ahora — no inventar. */}
      <div data-testid="workflow-notifications-slot" className="workflow-notifications-slot empty:hidden space-y-2" />
    </div>
  );
}

// Prompt AC CAMBIO 4 (2026-09-21): Alian — "tienes puesta esta informacion
// aqui y tienes puesta esta informacion aqui... hazme uno solo" — un unico
// bloque de datos del cliente, abajo, en 4 columnas (Bill To / Property
// Owner / Service Address / Project #). Reemplaza HeaderGrid (arriba) Y
// TermsRow (abajo) — no se duplica mas.
function BillToBlock({ invoice, customer, relations, onOpenProject }) {
  const ma = invoice.mailing_address || {};
  const pi = invoice.property_info || {};
  const project = relations?.project;
  return (
    <div data-testid="invoice-bill-to-block" className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
      <div data-testid="invoice-bill-to">
        <Label>Bill To</Label>
        <p className="mt-1.5 text-sm font-bold text-slate-900">{customer ? contactPrimaryLabel(customer) : '—'}</p>
        {customer?.phone && <p className="text-sm text-slate-700">{customer.phone}</p>}
        {customer?.email && <p className="text-sm text-slate-700 break-all">{customer.email}</p>}
        {ma.address && (
          <>
            <p className="text-sm text-slate-700 mt-0.5">{ma.address}</p>
            <p className="text-sm text-slate-700">{[ma.city, ma.state].filter(Boolean).join(', ')} {ma.zip || ''}</p>
          </>
        )}
      </div>
      <div data-testid="invoice-property-owner">
        <Label>Property Owner</Label>
        <p className="mt-1.5 text-sm font-bold text-slate-900">{pi.owner_name || '—'}</p>
      </div>
      <div data-testid="invoice-service-address">
        <Label>Service Address</Label>
        {invoice.well_address ? (
          <>
            <p className="mt-1.5 text-sm font-bold text-slate-900">{invoice.well_address}</p>
            <p className="text-sm text-slate-700">{[invoice.well_city, invoice.well_state].filter(Boolean).join(', ')} {invoice.well_zip || ''}</p>
            {pi.county && <p className="text-xs text-slate-500 mt-0.5">{pi.county} County</p>}
            {pi.parcel_id && <p className="text-xs text-slate-500 font-mono">Parcel: {pi.parcel_id}</p>}
          </>
        ) : (
          <p className="mt-1.5 text-sm text-slate-400">—</p>
        )}
      </div>
      <div data-testid="invoice-project-number">
        <Label>Project #</Label>
        {project ? (
          <button type="button" onClick={() => onOpenProject(project.id)} className="mt-1.5 text-left group" data-testid="invoice-project-link">
            <p className="text-sm font-bold text-slate-900 group-hover:text-[#003366] transition-colors">{project.title || '—'}</p>
            <p className="text-sm text-[#003366] font-semibold">Project # {project.number || '—'}</p>
          </button>
        ) : (
          <p className="mt-1.5 text-sm text-slate-400">—</p>
        )}
      </div>
    </div>
  );
}

function ItemsTable({ items }) {
  return (
    <div data-testid="invoice-items-block" className="pt-4 border-t border-slate-200">
      <Label>Items</Label>
      <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[520px]" data-testid="invoice-items-table">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300">
              <th className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2 w-10">#</th>
              <th className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Description</th>
              <th className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2 w-20">Qty</th>
              <th className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2 w-28">Unit Price</th>
              <th className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2 w-28">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {(items || []).length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-sm text-slate-400">No items</td></tr>
            )}
            {(items || []).map((item, idx) => (
              <tr key={idx} data-testid={`invoice-view-item-${idx}`}>
                <td className="px-3 py-2.5 text-sm text-slate-500 align-top">{idx + 1}</td>
                <td className="px-3 py-2.5 align-top">
                  <p className="text-sm font-semibold text-slate-900">{item.name || item.description || '—'}</p>
                  {item.description && item.description !== item.name && (
                    <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                  )}
                </td>
                <td className="px-3 py-2.5 text-sm font-mono text-slate-800 text-right align-top">{item.quantity || 0} <span className="text-slate-400 text-xs">{item.unit || 'unit'}</span></td>
                <td className="px-3 py-2.5 text-sm font-mono text-slate-800 text-right align-top">${fmtMoney(item.unit_price)}</td>
                <td className="px-3 py-2.5 text-sm font-mono font-bold text-slate-900 text-right align-top">${fmtMoney((item.quantity || 0) * (item.unit_price || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotalsPanel({ invoice }) {
  const Row = ({ label, value, stripe, valueClass, testid }) => (
    <div className={`flex items-center justify-between px-3 py-2 rounded-md border ${stripe}`} data-testid={testid}>
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      <span className={`font-mono tracking-tight ${valueClass}`}>${fmtMoney(value)}</span>
    </div>
  );
  return (
    <div data-testid="invoice-totals-panel" className="space-y-1.5">
      <Row label="Subtotal" value={invoice.subtotal} stripe="bg-slate-50 border-slate-200 text-slate-700" valueClass="text-sm font-bold text-slate-800" testid="totals-subtotal" />
      {(invoice.discount_amount || 0) > 0 && (
        <Row label="Discount" value={-invoice.discount_amount} stripe="bg-amber-50 border-amber-200 text-amber-700" valueClass="text-sm font-bold text-amber-700" testid="totals-discount" />
      )}
      {(invoice.surcharge || 0) > 0 && (
        <Row label={`Card Surcharge (${invoice.surcharge_rate || 0}%)`} value={invoice.surcharge} stripe="bg-amber-50 border-amber-200 text-amber-700" valueClass="text-sm font-bold text-amber-700" testid="totals-surcharge" />
      )}
      <Row label="Invoice Total" value={invoice.total} stripe="bg-blue-50/80 border-y border-r border-slate-200 border-l-4 border-l-blue-600 text-blue-950" valueClass="text-base font-extrabold text-blue-900" testid="totals-invoice-total" />
      <Row label="Total Paid" value={invoice.amount_paid} stripe="bg-emerald-50/80 border-y border-r border-slate-200 border-l-4 border-l-emerald-600 text-emerald-950" valueClass="text-base font-extrabold text-emerald-800" testid="totals-total-paid" />
      <Row label="Balance Due" value={invoice.balance_due} stripe="bg-rose-50/80 border-y border-r border-slate-200 border-l-4 border-l-rose-600 text-rose-950" valueClass="text-base font-black text-rose-700" testid="totals-balance-due" />
    </div>
  );
}

function PaymentHistory({ invoice, highlightPaymentId, onAddPayment, onSendReceipt }) {
  const payments = invoice.payments || [];
  let running = invoice.total || 0;
  const rows = payments.map((p, i) => {
    running = Math.max(0, Math.round((running - (p.amount || 0)) * 100) / 100);
    return { p, i, balance: running };
  });
  const canAdd = ['sent', 'partial', 'in_dispute', 'overdue'].includes(invoice.status);
  return (
    <div data-testid="payment-history-block" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">Payment History</h3>
        {canAdd && (
          <Button type="button" size="sm" variant="outline" onClick={() => onAddPayment()} data-testid="add-payment-btn" className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Payment
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="p-4 rounded-lg border border-dashed border-slate-300 text-center" data-testid="payment-history-empty">
          <p className="text-xs text-slate-400">No payments recorded yet</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[640px]" data-testid="payment-history-table">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-300">
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Date</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Method</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Reference #</th>
                <th className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Amount</th>
                <th className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-600 px-3 py-2">Balance</th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {rows.map(({ p, i, balance }) => {
                const isHighlighted = !!highlightPaymentId && p.id === highlightPaymentId;
                const recorded = (p.date || '').slice(0, 10);
                const received = (p.received_date || '').slice(0, 10) || recorded;
                const reference = p.payment_reference || p.reference || '';
                const methodClass = PAYMENT_METHOD_COLORS[p.method] || 'bg-slate-50 border-slate-200 text-slate-600';
                return (
                  <tr
                    key={p.id || i}
                    ref={(el) => { if (isHighlighted && el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                    data-testid={`invoice-payment-row-${p.id || i}`}
                    className={isHighlighted ? 'bg-amber-50 ring-2 ring-inset ring-amber-400' : ''}
                  >
                    <td className="px-3 py-2.5 align-top">
                      <p className="text-sm font-medium text-slate-900 whitespace-nowrap" data-testid={`invoice-payment-dates-${p.id || i}`}>{shortDate(received)}</p>
                      {recorded && recorded !== received && <p className="text-[10px] text-slate-400">recorded {shortDate(recorded)}</p>}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${methodClass}`}>
                        {PAYMENT_METHOD_LABELS[p.method] || p.method || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className={`text-sm font-mono ${reference ? 'text-slate-900' : 'text-slate-400'}`} data-testid={`payment-reference-${p.id || i}`}>{reference || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right align-top">
                      <p className="text-sm font-mono font-bold text-slate-900">${fmtMoney(p.amount)}</p>
                      {p.surcharge_amount > 0 && (
                        <p className="text-[10px] text-amber-600" data-testid={`invoice-payment-surcharge-${p.id || i}`}>+${fmtMoney(p.surcharge_amount)} surcharge (${fmtMoney(p.total_charged)} charged)</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono font-semibold text-slate-700 text-right align-top whitespace-nowrap">${fmtMoney(balance)}</td>
                    <td className="px-3 py-2.5 text-right align-top">
                      <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#003366] hover:bg-blue-50" onClick={() => onSendReceipt(p.id || null)} data-testid={`payment-receipt-btn-${p.id || i}`}>
                        <Receipt className="w-3.5 h-3.5 mr-1" /> Receipt
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentRequests({ invoice, onChanged, onNewRequest, onMarkPaid, onDeleteRequest }) {
  const prs = invoice.payment_requests || [];
  const prStatusColors = {
    pending: 'bg-amber-50 border-amber-200 text-amber-700',
    sent: 'bg-blue-50 border-blue-200 text-blue-700',
    paid: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    cancelled: 'bg-slate-50 border-slate-200 text-slate-400',
  };
  const prTypeLabels = {
    down_payment: 'Down Payment', progress_payment: 'Progress Payment',
    final_payment: 'Final Payment', full_payment: 'Total Amount Due',
  };
  const setStatus = async (pr, status, msg) => {
    try {
      await api.put(`/invoices/${invoice.id}/payment-request/${pr.id}`, { status });
      toast.success(msg);
      onChanged();
    } catch { toast.error('Error'); }
  };
  const active = prs.filter(pr => pr.status !== 'cancelled');
  const totalRequested = active.reduce((s, pr) => s + (pr.amount || 0), 0);
  const totalPaid = active.filter(pr => pr.status === 'paid').reduce((s, pr) => s + (pr.amount || 0), 0);
  const unrequested = Math.max(0, (invoice.total || 0) - totalRequested);
  return (
    <div data-testid="payment-requests-section" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
          <CreditCard className="w-4 h-4 text-slate-500" /> Payment Requests
        </h3>
        {invoice.balance_due > 0 && (
          <Button type="button" size="sm" variant="outline" data-testid="create-payment-request-btn" onClick={onNewRequest} className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Request
          </Button>
        )}
      </div>
      {prs.length > 0 ? (
        <div className="space-y-2">
          {prs.map((pr, i) => {
            const totalBeforeThis = prs.slice(0, i).filter(p => p.status !== 'cancelled').reduce((s, p) => s + (p.amount || 0), 0);
            const balanceAfter = (invoice.total || 0) - totalBeforeThis - (pr.amount || 0);
            return (
              <div key={pr.id} data-testid={`payment-request-${pr.id}`} className={`p-3 rounded-lg border ${prStatusColors[pr.status] || 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900">{pr.label || prTypeLabels[pr.request_type] || 'Payment'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                        pr.status === 'paid' ? 'bg-emerald-200 text-emerald-800' :
                        pr.status === 'sent' ? 'bg-blue-200 text-blue-800' :
                        pr.status === 'cancelled' ? 'bg-slate-200 text-slate-600' :
                        'bg-amber-200 text-amber-800'
                      }`}>{pr.status}</span>
                    </div>
                    {pr.notes && <p className="text-xs text-slate-500 mt-0.5">{pr.notes}</p>}
                    {pr.due_date && <p className="text-[10px] text-slate-400 mt-0.5">Due: {pr.due_date.slice(0, 10)}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-bold text-slate-900">${fmtMoney(pr.amount)}</p>
                    <p className="text-[10px] text-slate-500">Balance: ${fmtMoney(Math.max(0, balanceAfter))}</p>
                  </div>
                </div>
                {pr.status === 'pending' && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200">
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-blue-600 hover:text-blue-800 px-2" onClick={() => setStatus(pr, 'sent', 'Sent!')}>
                      <Send className="w-3 h-3 mr-1" /> Send
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-red-500 hover:text-red-700 px-2" onClick={() => onDeleteRequest(pr.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                    <span className="ml-auto text-[10px] text-slate-400 font-mono">{pr.public_token}</span>
                  </div>
                )}
                {pr.status === 'sent' && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200">
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-600 px-2" data-testid={`mark-paid-pr-${pr.id}`} onClick={() => onMarkPaid(pr)}>
                      <Check className="w-3 h-3 mr-1" /> Mark Paid
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-red-500 hover:text-red-700 px-2" onClick={() => onDeleteRequest(pr.id)} data-testid={`delete-pr-${pr.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                    <span className="ml-auto text-[10px] text-slate-400 font-mono">{pr.public_token}</span>
                  </div>
                )}
                {pr.status === 'paid' && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200">
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-amber-600 px-2" onClick={() => setStatus(pr, 'sent', 'Payment reverted to Sent')} data-testid={`undo-paid-${pr.id}`}>
                      <ArrowLeft className="w-3 h-3 mr-1" /> Undo Paid
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-red-500 hover:text-red-700 px-2" onClick={() => onDeleteRequest(pr.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                    <span className="ml-auto text-[10px] text-slate-400 font-mono">{pr.public_token}</span>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-center justify-between text-xs p-2 rounded bg-slate-50 border border-slate-200">
            <span className="text-slate-600">Total: <b className="text-slate-900">${fmtMoney(invoice.total)}</b></span>
            <span className="text-blue-700">Requested: <b>${fmtMoney(totalRequested)}</b></span>
            <span className="text-emerald-700">Paid: <b>${fmtMoney(totalPaid)}</b></span>
            {unrequested > 0 && <span className="text-amber-700">Unrequested: <b>${fmtMoney(unrequested)}</b></span>}
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-lg border border-dashed border-slate-300 text-center">
          <p className="text-xs text-slate-400">No payment requests yet</p>
          <p className="text-[10px] text-slate-400 mt-1">Create payment requests to collect partial payments (down payment, progress, final)</p>
        </div>
      )}
    </div>
  );
}

export default function InvoiceDetailView({
  invoice, customer, relations, highlightPaymentId,
  canEditStatus,
  onEdit, onSend, onRecordPayment, onSendReceipt, onDispute, onCancel,
  onStatusChange, onChanged, onNewPaymentRequest, onDeletePaymentRequest,
}) {
  const navigate = useNavigate();
  const [previewing, setPreviewing] = useState(false);
  const status = invoice.status || 'draft';
  const qualifier = paymentRoundQualifier(invoice);

  const handleNavigate = (kind, id) => {
    if (kind === 'scroll') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (kind === 'estimate') {
      navigate('/estimates', { state: { openEstimateId: id } });
    } else if (kind === 'job') {
      navigate(`/jobs/${id}`);
    } else if (kind === 'permit') {
      navigate(`/permits?open=${id}`);
    }
  };

  const handlePreviewPrint = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/pdf/invoice/${invoice.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('No se pudo generar el PDF'); }
    finally { setPreviewing(false); }
  };

  return (
    <div data-testid="invoice-detail-view" className="space-y-4">
      <DialogHeader className="sr-only">
        <DialogTitle>Invoice {invoice.invoice_number}</DialogTitle>
      </DialogHeader>

      <QuickLinksBar entityType="invoice" relations={relations} customer={customer} className="px-1" testId="invoice-quick-links" />

      <DocTabs invoice={invoice} relations={relations} onNavigate={handleNavigate} />

      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4" data-testid="invoice-main-card">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight font-['Barlow']">Invoice</h2>
            <span data-testid="invoice-status-pill" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wide ${STATUS_PILL[status] || STATUS_PILL.draft}`}>
              {STATUS_LABEL[status] || status}
            </span>
            {qualifier && <span data-testid={`invoice-payment-round-${invoice.id}`} className="text-xs font-medium text-slate-500">— {qualifier}</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1" data-testid="invoice-meta-line">
            Invoice # <span className="font-semibold text-slate-900">{invoice.invoice_number}</span>
            {' · '}Date {shortDate(invoice.created_at)}
            {' · '}Due {shortDate(invoice.due_date)}
          </p>
        </div>

        <WorkflowBlock invoice={invoice} canEditStatus={canEditStatus} onStatusChange={onStatusChange} />

        {status === 'cancelled' && invoice.cancellation_reason && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200" data-testid="invoice-cancelled-banner">
            <div className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-700">Invoice Cancelled</p>
                <p className="text-xs text-red-600 mt-0.5">{invoice.cancellation_reason}</p>
                {invoice.cancelled_at && <p className="text-[10px] text-red-400 mt-1">{invoice.cancelled_at.slice(0, 10)}</p>}
              </div>
            </div>
          </div>
        )}
        {status === 'in_dispute' && invoice.dispute_reason && (
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid="invoice-dispute-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-rose-700">In Dispute</p>
                <p className="text-xs text-rose-600 mt-0.5">{invoice.dispute_reason}</p>
                {invoice.disputed_at && <p className="text-[10px] text-rose-400 mt-1">{invoice.disputed_at.slice(0, 10)}</p>}
              </div>
            </div>
          </div>
        )}

        <BillToBlock invoice={invoice} customer={customer} relations={relations} onOpenProject={(id) => navigate(`/projects/${id}`)} />

        <ItemsTable items={invoice.items} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 border-t border-slate-200" id="invoice-section-notes">
          <div className="lg:col-span-7" data-testid="invoice-notes-box">
            <Label>Notes</Label>
            <div className="mt-2 min-h-[120px] p-3 rounded-lg border border-slate-300 bg-white">
              {invoice.notes ? (
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{invoice.notes}</p>
              ) : (
                <p className="text-sm text-slate-400">—</p>
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            <TotalsPanel invoice={invoice} />
          </div>
        </div>
      </div>

      <PaymentHistory
        invoice={invoice}
        highlightPaymentId={highlightPaymentId}
        onAddPayment={onRecordPayment}
        onSendReceipt={onSendReceipt}
      />

      {status !== 'cancelled' && (
        <PaymentRequests
          invoice={invoice}
          onChanged={onChanged}
          onNewRequest={onNewPaymentRequest}
          onMarkPaid={onRecordPayment}
          onDeleteRequest={onDeletePaymentRequest}
        />
      )}

      <div id="invoice-section-files" data-testid="invoice-files-section" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
        <DocumentsSection entityType="invoice" entityId={invoice.id} />
      </div>

      <div id="invoice-section-history" data-testid="invoice-history-section" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
        <ActivityLog entityType="invoices" entityId={invoice.id} />
      </div>

      <div data-testid="invoice-action-bar" className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-t-2 border-slate-200 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handlePreviewPrint} disabled={previewing} data-testid="action-preview-print">
          <Printer className="w-3.5 h-3.5 mr-1.5" /> {previewing ? 'Generating…' : 'Preview / Print'}
        </Button>
        {!['paid', 'cancelled'].includes(status) && (
          <Button type="button" variant="outline" size="sm" onClick={onEdit} data-testid="edit-mode-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onSend} data-testid="detail-send-invoice" className="border-[#003366]/30 text-[#003366]">
          <Send className="w-3.5 h-3.5 mr-1.5" /> {status === 'draft' ? 'Email Invoice' : 'Resend'}
        </Button>
        {['sent', 'partial', 'in_dispute', 'overdue'].includes(status) && (
          <Button type="button" size="sm" onClick={() => onRecordPayment()} data-testid="detail-collect-payment" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Record Payment
          </Button>
        )}
        {(invoice.payments || []).length > 0 && (
          <Button type="button" variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => onSendReceipt(null)} data-testid="detail-send-receipt">
            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Send Receipt
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!['paid', 'cancelled', 'in_dispute'].includes(status) && (
            <Button type="button" variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={onDispute} data-testid="detail-dispute-invoice">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> In Dispute
            </Button>
          )}
          {!['paid', 'cancelled'].includes(status) && canEditStatus && (
            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={onCancel} data-testid="detail-cancel-invoice">
              <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
